import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import {
  SocialPostItem,
  SocialPostsSort,
  SocialSyncRunItem,
  SocialTrackerItem,
  SocialTrackersResponse,
} from '@digital-twins/shared-types'
import { useSocialListeningApi } from './SocialListeningApiContext'
import { onSocialTrackerUpdated } from '@/lib/services/notifications-socket'

export const SOCIAL_TRACKERS_SWR_KEY = 'social-listening/trackers'

const POSTS_PAGE_SIZE = 25

interface UseSocialTrackersResult {
  isEnabled: boolean
  canManage: boolean
  trackers: Array<SocialTrackerItem>
  isLoading: boolean
  hasError: boolean
  refresh: () => Promise<SocialTrackersResponse | undefined>
  /** Swap one tracker in the cached list (mutation responses return the item). */
  replaceTracker: (tracker: SocialTrackerItem) => void
  removeTracker: (token: string) => void
}

/**
 * Tracker list for the Library's Social media tab and the Ask tracker card.
 * Refetches whenever the notifications socket says a sync changed a tracker's
 * status. A failed request reads as "not enabled" so the Library looks exactly
 * as it does without the feature.
 */
export function useSocialTrackers(shouldLoad: boolean): UseSocialTrackersResult {
  const socialListeningApi = useSocialListeningApi()
  const { data, error, isLoading, mutate } = useSWR<SocialTrackersResponse>(
    shouldLoad ? SOCIAL_TRACKERS_SWR_KEY : null,
    () => socialListeningApi.listTrackers(),
    { revalidateOnFocus: false },
  )
  const isEnabled = Boolean(data?.enabled) && !error

  useEffect(() => {
    if (!shouldLoad || !isEnabled) {
      return
    }
    return onSocialTrackerUpdated(() => {
      void mutate()
    })
  }, [shouldLoad, isEnabled, mutate])

  const replaceTracker = useCallback(
    (tracker: SocialTrackerItem) => {
      void mutate(
        (current) => {
          if (!current) {
            return current
          }
          const exists = current.trackers.some((item) => item.token === tracker.token)
          return {
            ...current,
            trackers: exists
              ? current.trackers.map((item) => (item.token === tracker.token ? tracker : item))
              : [tracker, ...current.trackers],
          }
        },
        { revalidate: false },
      )
    },
    [mutate],
  )

  const removeTracker = useCallback(
    (token: string) => {
      void mutate(
        (current) =>
          current
            ? { ...current, trackers: current.trackers.filter((item) => item.token !== token) }
            : current,
        { revalidate: false },
      )
    },
    [mutate],
  )

  return {
    isEnabled,
    canManage: Boolean(data?.canManage),
    trackers: data?.trackers ?? [],
    isLoading: shouldLoad && isLoading,
    hasError: Boolean(error),
    refresh: () => mutate(),
    replaceTracker,
    removeTracker,
  }
}

interface UseSocialTrackerPostsResult {
  posts: Array<SocialPostItem>
  isLoading: boolean
  isLoadingMore: boolean
  hasError: boolean
  hasMore: boolean
  loadMore: () => void
  removePosts: (predicate: (post: SocialPostItem) => boolean) => void
}

/**
 * Cursor-paginated posts for one tracker; reloads when the tracker, the order
 * or its last sync (`syncedAt`) changes.
 */
export function useSocialTrackerPosts(
  trackerToken: string | null,
  sort: SocialPostsSort = 'newest',
  syncedAt: string | null = null,
  topicId: string | null = null,
): UseSocialTrackerPostsResult {
  const socialListeningApi = useSocialListeningApi()
  const [posts, setPosts] = useState<Array<SocialPostItem>>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasError, setHasError] = useState(false)
  const generationRef = useRef(0)

  useEffect(() => {
    const generation = ++generationRef.current
    setPosts([])
    setNextCursor(null)
    setHasError(false)
    if (!trackerToken) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    socialListeningApi
      .listPosts(trackerToken, { limit: POSTS_PAGE_SIZE, sort, topicId })
      .then((page) => {
        if (generation !== generationRef.current) {
          return
        }
        setPosts(page.posts)
        setNextCursor(page.nextCursor)
      })
      .catch(() => {
        if (generation === generationRef.current) {
          setHasError(true)
        }
      })
      .finally(() => {
        if (generation === generationRef.current) {
          setIsLoading(false)
        }
      })
  }, [socialListeningApi, trackerToken, sort, syncedAt, topicId])

  function loadMore() {
    if (!trackerToken || !nextCursor || isLoadingMore) {
      return
    }
    const generation = generationRef.current
    setIsLoadingMore(true)
    socialListeningApi
      .listPosts(trackerToken, { cursor: nextCursor, limit: POSTS_PAGE_SIZE, sort, topicId })
      .then((page) => {
        if (generation !== generationRef.current) {
          return
        }
        setPosts((prev) => [...prev, ...page.posts])
        setNextCursor(page.nextCursor)
      })
      .catch(() => {
        if (generation === generationRef.current) {
          setHasError(true)
        }
      })
      .finally(() => {
        if (generation === generationRef.current) {
          setIsLoadingMore(false)
        }
      })
  }

  function removePosts(predicate: (post: SocialPostItem) => boolean) {
    setPosts((prev) => prev.filter((post) => !predicate(post)))
  }

  return {
    posts,
    isLoading,
    isLoadingMore,
    hasError,
    hasMore: Boolean(nextCursor),
    loadMore,
    removePosts,
  }
}

interface UseSocialTrackerRunsResult {
  runs: Array<SocialSyncRunItem>
  isLoading: boolean
  hasError: boolean
}

export function useSocialTrackerRuns(trackerToken: string | null): UseSocialTrackerRunsResult {
  const socialListeningApi = useSocialListeningApi()
  const { data, error, isLoading } = useSWR(
    trackerToken ? ['social-listening/runs', trackerToken] : null,
    () => socialListeningApi.listRuns(trackerToken as string),
    { revalidateOnFocus: false },
  )
  return {
    runs: data?.runs ?? [],
    isLoading,
    hasError: Boolean(error),
  }
}
